/**
 * Preço e benefícios do PRO/Founder — FONTE ÚNICA do client.
 *
 * ── Por que este arquivo existe ─────────────────────────────────────────────
 * Até 08/2026 o preço na tela era texto solto dentro do JSX. Quem cobra é o
 * Stripe (STRIPE_PRICE_MONTHLY / _ANNUAL / _FOUNDER_TIER1 / _TIER2 no env), e
 * o client não lê nada disso — então os dois podiam divergir para sempre sem
 * erro. Divergiam: a `/pro` anunciava R$ 9,90/mês, R$ 95,00/ano e Founder
 * R$ 199,90, enquanto a `/founder` anunciava R$ 249 pela MESMA compra e o
 * cartão foi debitado em R$ 14,99 no teste de 28/07.
 *
 * ⚠️ Este módulo NÃO define o que é cobrado — define o que é ANUNCIADO.
 * A verdade da cobrança mora no painel do Stripe. Manter os dois alinhados é
 * passo de deploy, não de código: ver `docs/qa-pendente.md`.
 *
 * Valores conforme decisão do fundador (01/08/2026, reconfirmada 02/08).
 */

/** Preços anunciados, já formatados em pt-BR. */
export const PRO_PRICE = {
  /** Mensal recorrente. */
  monthly: "R$ 14,90",
  /** Anual recorrente — cobrado À VISTA, uma vez. */
  annual: "R$ 119,90",
  /**
   * Equivalente mensal do anual. É CONTA, não preço cobrado: a página é
   * obrigada a dizer que o anual é à vista, senão vira preço-isca (decisão
   * registrada em pricing.md §8.3).
   */
  annualPerMonth: "R$ 9,99",
  /** Economia do anual contra 12× o mensal (14,90×12 = 178,80 → 119,90). */
  annualSavings: "33%",
  /** Founder vitalício, pagamento único. */
  founderTier1: "R$ 249",
  founderTier2: "R$ 299",
} as const;

/** Valores numéricos — para contas (comparativo Founder × assinar). */
export const PRO_PRICE_NUM = {
  monthly: 14.9,
  annual: 119.9,
  founderTier1: 249,
  founderTier2: 299,
} as const;

/** Dias de teste grátis. Espelho de `TRIAL_DAYS` em createCheckoutSession.ts
 *  — vale nos DOIS planos recorrentes (o anual ganhou o trial em 16/09; até
 *  então entrava cobrado). Uma vez por pessoa: quem já testou não vê o número. */
export const PRO_TRIAL_DAYS = 7;

/**
 * Limites da série Founder. Espelho de `TIER1_THRESHOLD` e do `cap` default
 * em functions/src/billing/founderCheckout.ts — há teste travando o par.
 */
export const FOUNDER_TIER1_THRESHOLD = 3000;
export const FOUNDER_CAP = 5000;

/** Tier vigente e preço, derivados das vagas RESERVADAS (`sold`). */
export function founderTierFor(sold: number): 1 | 2 {
  return sold < FOUNDER_TIER1_THRESHOLD ? 1 : 2;
}
export function founderPriceFor(sold: number): string {
  return founderTierFor(sold) === 1 ? PRO_PRICE.founderTier1 : PRO_PRICE.founderTier2;
}
export function founderPriceNumFor(sold: number): number {
  return founderTierFor(sold) === 1 ? PRO_PRICE_NUM.founderTier1 : PRO_PRICE_NUM.founderTier2;
}

// ── Benefícios ───────────────────────────────────────────────────────────────

export interface ProBenefit {
  /**
   * Slug estável (Bloco H, Admin-Alavancas — `docs/systems/admin-pro-showcase.md`
   * §2.2). NUNCA recalculado de `title`, que é justamente o campo editável
   * pelo painel — é o que permite ao overlay distinguir "renomeou o
   * benefício" de "trocou por outro". Espelhado no servidor em
   * `functions/src/shared/proShowcaseIds.ts`, comparado por
   * `tests/functions/proShowcaseIdsParity.test.ts`.
   */
  id: string;
  /** Nome do ícone lucide, resolvido no componente. */
  icon:
    | "image"
    | "gem"
    | "music"
    | "bookImage"
    | "palette"
    | "atSign"
    | "sparkles"
    | "share"
    | "moon"
    | "coins"
    | "link"
    | "chart"
    | "trophy";
  title: string;
  description: string;
  /** Onde o benefício é entregue no código — a auditoria mora aqui. */
  source: string;
}

/**
 * ── REGRA: nenhum benefício aqui que o código não entregue HOJE ─────────────
 * (delta 1 do fundador, 01/08). Cada linha foi conferida contra o código em
 * 02/08 e o `source` diz onde. Três anúncios da página antiga foram REMOVIDOS
 * por não passarem nessa conferência:
 *
 *  · "Streak Protegido — recupere com tickets mensais": o PRO não CONCEDE
 *    `streakTickets`. Em 02/08 nada concedia; desde as Missões v2 (16/09) a
 *    fonte é o baú/missão semanal, 1 por semana, igual para todos
 *    (`functions/src/quests/ledger.ts`). Quem assinasse por isso não receberia
 *    nada a mais — e a tela da Ofensiva dizer o contrário foi o BUG-284.
 *  · "Modelos de IA melhores": `modelPolicy.proModelsEnabled()` é OFF por
 *    default e `AI_PRO_MODELS_ENABLED` não aparece em nenhum env do repo — no
 *    modo econômico TODOS os tiers recebem a cadeia flash, e o chat usa flash
 *    pra todo mundo mesmo com a flag ligada. Volta pra cá quando a flag ligar
 *    em prod, não antes.
 *  · "Acesso antecipado a novos recursos" e "card com glassmorphism
 *    exclusivo": não achei mecânica nenhuma por trás.
 *
 * E um foi CORRIGIDO: o card do PRO não sai "sem marca d'água" — sai com uma
 * marca menor. Prometer card limpo era vender o que não existe.
 *
 * ── ORDEM: identidade primeiro, economia por último (03/09) ────────────────
 *
 * A lista abriu por IA e moedas até 03/09, e a medição em produção mostrou o
 * desencontro: o que o assinante de fato usa é IDENTIDADE — 7 dos 12 PRO têm
 * banner, e banner, música do perfil, avatar animado e cor do nome **não
 * estavam anunciados em lugar nenhum**. Vender desconto e anunciar nada do que
 * aparece no perfil é vender o produto errado.
 *
 * Saiu junto "Cinco listas na vitrine": o gate é real (2 free / 5 PRO), mas
 * existem 13 listas na base inteira — ninguém chega perto do teto, então o
 * benefício não morde. Volta se o comportamento aparecer.
 *
 * E "+50% de moedas" e "30% off" viraram UM item: são a mesma economia, e
 * empilhados no topo faziam o PRO parecer um cupom. O desconto caiu para 15%
 * no mesmo dia (ver `PRO_SHOP_DISCOUNT`).
 *
 * ⚠️ A regra de cima continua valendo linha a linha: o `source` de cada item
 * foi reconferido contra o código em 03/09, e o da marca d'água só entrou
 * porque ela **passou a existir** no mesmo commit (`CardBrandTierContext`) —
 * até então ela apontava para um arquivo apagado em 06/08.
 *
 * 04/09: entrou "Capas próprias sem limite", pela mesma razão que os outros
 * quatro de identidade entraram — o gate passou a EXISTIR no mesmo commit
 * (antes o teto era diário e ninguém o alcançava, então não havia diferença de
 * plano para anunciar). Os quatro primeiros itens são agora as superfícies que
 * a pessoa mostra: perfil (banner/avatar · nome · música) e estante (capas).
 */
export const PRO_BENEFITS: readonly ProBenefit[] = [
  {
    id: "banner_avatar",
    icon: "image",
    title: "Banner e avatar animados",
    description:
      "Sua capa no topo do perfil e um avatar em GIF. É a primeira coisa que quem visita vê.",
    source:
      "functions/src/account/profileImage.ts:299 — banner recusado sem isPro · " +
      "SettingsIdentityCard.tsx:311 — accept de GIF/Lottie",
  },
  {
    id: "cosmetics",
    icon: "gem",
    title: "Cor do nome, flairs e a moldura Cristal",
    description:
      "Seu nome sai colorido em toda tela do app, com os flairs e a moldura que só assinante tem.",
    source:
      "functions/src/cosmetics/setActiveCosmetic.ts:98 — usernameColor exige isPro · " +
      "store_items.proExclusive (border_cristal, pro_reader, curator, lura_og)",
  },
  {
    id: "profile_music",
    icon: "music",
    title: "Uma música no seu perfil",
    description: "Escolha uma faixa do Spotify pra tocar pra quem abrir seu perfil.",
    source: "functions/src/account/profileMusic.ts:155 e spotifySearch.ts:279 — isPro !== true recusa",
  },
  {
    id: "custom_covers",
    icon: "bookImage",
    title: "Capas próprias sem limite",
    /*
     * Entrou em 04/09, quando o teto da capa própria mudou de eixo: era 5
     * trocas POR DIA no free contra 20 no PRO, um número que ninguém encosta —
     * a troca de capa é gesto raro, e um gate invisível não é gate. Agora o
     * free tem UMA capa de upload por vez na estante e o PRO não tem teto de
     * acervo, que é a diferença permanente e visível para quem abre o perfil.
     *
     * ⚠️ A frase diz "suas" de propósito: a capa de OUTRA EDIÇÃO segue livre
     * para todo mundo, sem teto. Anunciar "troque as capas da sua estante"
     * venderia como PRO o que o free já tem.
     */
    description:
      "Ponha a sua imagem na capa de quantos livros quiser. No plano gratuito é uma por vez.",
    source:
      "functions/src/books/userBookCover.ts — FREE_CUSTOM_COVER_UPLOADS 1, " +
      "sem teto de acervo para isPro",
  },
  {
    id: "themes",
    icon: "palette",
    // 27/09: "Seis" envelheceu — Alcova, Sakura, Vitral, Romantasy e Dark
    // Academia entraram depois de 03/09. Latão é `founder`, não conta.
    title: "Dez temas exclusivos",
    description: "Sakura, Vitral, Romantasy, Dark Academia e mais seis — nenhum se compra com moedas.",
    source: "src/config/themeRegistry.ts — 8 tier 'pro' + 2 tier 'premium' (medido 27/09; latao é 'founder')",
  },
  {
    id: "handle_change",
    icon: "atSign",
    title: "Troque seu @ quando quiser",
    description: "Um por dia, contra 60 dias de espera de quem é free.",
    source: "functions/src/account/claimUsername.ts:86 — COOLDOWN_DAYS_FREE 60 · COOLDOWN_DAYS_PRO 1",
  },
  {
    id: "ai_limit",
    icon: "sparkles",
    // 27/09: o limite de pedidos era a única diferença anunciada da Lura; o
    // lote maior e a cadência semanal (o que aparece na Home) não estavam.
    title: "A Lura completa",
    description: "5 escolhas por lote em vez de 2, curadoria nova toda semana (não uma vez por mês) e 20 pedidos por dia em vez de 1.",
    source:
      "functions/src/ai/prompt.ts:13 aiDisplayTargetForTier (5 × 2) · " +
      "functions/src/discovery/personalAiCuration.ts cadenceForTier (semanal × 28 dias, 06/10) · " +
      "functions/src/ai/functions.ts AI_DAILY_LIMIT (20 × 1, 06/10)",
  },
  {
    id: "watermark",
    icon: "share",
    title: "Marca d'água enxuta nos cards",
    /*
     * ⚠️ **"A maioria" é literal, e a ressalva não é hedge de copy.**
     *
     * 📌 Decisão do fundador (03/09): a marca do Livra volta para os dois tiers
     * no card do **Foco** e no card de **evento** (Bienal). Os dois são peça de
     * circulação — o de Foco é o mais compartilhado do app, e o de evento
     * existe para ser postado —, e o mock do card de Foco sempre pediu
     * *"wordmark Livra"*: ali a marca é fidelidade recuperada, não exceção.
     *
     * A frase mudou junto no MESMO commit. Prometer "seus cards" e entregar 15
     * de 17 é o tipo de imprecisão que este projeto já pagou caro (o preço da
     * `/pro` divergindo do checkout, o "vitalício" indefinido nos Termos): quem
     * assina por causa de um benefício e descobre a exceção depois não lê isso
     * como detalhe, lê como propaganda enganosa.
     */
    description:
      "A maioria dos seus cards sai só com uselivra.com no rodapé, sem o selo LIVRA.",
    source:
      "v3Kit.tsx V3BrandZone + ProfileV3Layout — via CardBrandTierContext · " +
      "exceções: FocusV3Layout e BienalV3Layout desenham a marca no TOPO, nos " +
      "dois tiers, e não consultam o contexto (anatomia invertida, 04-05/09)",
  },
  {
    id: "focus_scenes",
    icon: "moon",
    title: "Cenas fechadas do Foco",
    description: "Campo de Cerejeiras e Nevasca — atmosferas que não estão à venda por moedas.",
    source: "src/features/focus/config/focusSceneRegistry.ts — 2 cenas tier 'pro'",
  },
  {
    id: "coins_bonus",
    icon: "coins",
    title: "Mais moedas, e a loja mais barata",
    // 13/09: "cada recompensa" virou "Foco e ofensiva" — o prêmio de liga saiu
    // do bônus e o do Foco passou a caber no mesmo teto diário de todo mundo.
    // Vender "cada recompensa" e entregar duas é a classe de erro da marca
    // d'água (03/09): promessa maior que o produto.
    description:
      "Suas sessões de Foco e sua ofensiva rendem uma vez e meia, e tudo na loja sai 15% menor.",
    source:
      "functions/src/shared/proCoinBonus.ts — ×1,5 · focusReward.ts computeFocusCoinCapAward (dentro do teto) · " +
      "economyTx.ts PRO_BONUS_EXEMPT_REASONS (liga fora) · " +
      "functions/src/cosmetics/types.ts — PRO_SHOP_DISCOUNT 0.15",
  },
  // ── 27/09 — o que o PRO já entregava e a /pro não dizia (estudo de
  // divulgação do PRO, `docs/product/estudo-pro-divulgacao.md` §3.3). Os quatro
  // gates foram conferidos no código no mesmo dia; entram no FIM da lista para
  // não mudar os três primeiros (o `ProTeaserModal` mostra só esses).
  {
    id: "reading_theme",
    icon: "palette",
    title: "O tema da sua leitura",
    description: "O app e o seu perfil ganham as cores da capa do livro que você está lendo.",
    source: "functions/src/account/generateReadingTheme.ts:73 — recusa sem isPro",
  },
  {
    id: "lendo_page",
    icon: "link",
    title: "Sua página /lendo",
    description: "Um link para a bio com o que você está lendo agora, a nota e a resenha.",
    source: "src/pages/PublicReadingPage.tsx:84 — dono sem isPro vê 'A /lendo é exclusiva do PRO'",
  },
  {
    id: "full_stats",
    icon: "chart",
    title: "Estatísticas completas",
    description: "Todas as comparações com os leitores do Livra, não só a manchete do mês.",
    source: "firestore.rules:39 isCompareReadable — compare/pro exige isPro (free lê só 'headline')",
  },
  {
    id: "private_leagues",
    icon: "trophy",
    title: "Ligas privadas",
    description: "Crie a sua liga e chame quem você quiser para competir.",
    source: "functions/src/leagues/functions.ts:63 — criar liga privada exige isPro",
  },
] as const;

/**
 * Os três grupos da `/pro` (C1 do mock `redesign-livra/pro-divulgacao/`,
 * aprovado 27/09): identidade, leitura, vantagens. Só a ARRUMAÇÃO mora aqui —
 * título, descrição e ordem dentro do grupo continuam vindo de `PRO_BENEFITS`
 * (e do overlay do painel). Id sem grupo cai no último, nunca some.
 */
export const PRO_BENEFIT_GROUPS: readonly { label: string; ids: readonly string[] }[] = [
  { label: "Seu perfil", ids: ["banner_avatar", "cosmetics", "profile_music", "handle_change"] },
  {
    label: "Sua leitura",
    ids: ["reading_theme", "themes", "lendo_page", "full_stats", "focus_scenes", "ai_limit"],
  },
  { label: "Vantagens", ids: ["private_leagues", "custom_covers", "watermark", "coins_bonus"] },
];

/**
 * Só os ids — espelhado no servidor (`functions/src/shared/proShowcaseIds.ts`)
 * porque `functions/` e `src/` são dois bundles sem import cruzado nesta casa.
 * Paridade travada por `tests/functions/proShowcaseIdsParity.test.ts`.
 */
export const PRO_BENEFIT_IDS: readonly string[] = PRO_BENEFITS.map((b) => b.id);

/** O que o Founder tem ALÉM do PRO. */
export const FOUNDER_EXTRAS: readonly { icon: "crown" | "receipt" | "star"; title: string; description: string }[] = [
  {
    icon: "crown",
    title: "Tudo do PRO, pra sempre",
    description: "Inclusive o que o PRO ganhar depois. Sem renovação, sem cartão vencendo.",
  },
  {
    icon: "receipt",
    title: "A placa numerada no perfil",
    description: "Seu número aparece pra quem visita — e é só seu, dentro de 5.000.",
  },
  {
    icon: "star",
    title: "Tier de exibição próprio",
    description: "Founder não se confunde com PRO: o selo e a cor do perfil são outros.",
  },
];

/** Perguntas do FAQ. Respostas conferidas contra o comportamento real. */
/**
 * ⚠️ Ordem: o trial vem PRIMEIRO desde a F1 do Roadmap PRO (11/09) — antes,
 * "posso cancelar quando quiser" abria a lista e o trial vinha em 2º lugar,
 * o que enterrava a pergunta que mais reduz a fricção de quem nunca assinou
 * nada. A pergunta de cancelamento continua verdadeira e fica logo depois.
 */
export const PRO_FAQ: readonly [string, string][] = [
  [
    `Como funcionam os ${PRO_TRIAL_DAYS} dias grátis?`,
    `Valem no mensal e no anual: os ${PRO_TRIAL_DAYS} dias correm antes da primeira cobrança. Cancelou dentro deles, não paga nada. É um teste por pessoa.`,
  ],
  [
    "Posso cancelar quando quiser?",
    "Pode, e o botão fica nesta mesma página. Você segue PRO até o fim do período já pago e não é cobrado de novo. Nada de ligar pra ninguém.",
  ],
  [
    "O anual é parcelado?",
    `Não. São ${PRO_PRICE.annual} cobrados uma vez, à vista. O ${PRO_PRICE.annualPerMonth} é só a conta de quanto isso dá por mês.`,
  ],
  [
    "Como eu pago?",
    "Cartão de crédito pelo Stripe, em português. Trocar cartão, ver recibos e cancelar ficam no portal do próprio Stripe.",
  ],
];
